import { Checkbox } from '@repo/go-ui';
import { useTranslation } from 'react-i18next';

import type { ReadonlyDeep } from '../../types/readonly-deep';
import type { TTrajectory } from '../../types/trajectory';

import { formatTrajectoryMeasure, formatTrajectoryText } from './format-trajectory-measure';
import styles from './trajectory-list-sidebar.module.scss';

type Props = {
  trajectory: ReadonlyDeep<TTrajectory> | null;
  parentTitle?: string | null;
};

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value}</span>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>{label}</span>
      <span className={styles.rowValue}>{value}</span>
    </div>
  );
}

function resolveDepthUom(trajectory: ReadonlyDeep<TTrajectory>): string {
  return (
    trajectory.mdTieOn?.measuredDepth.uom ||
    trajectory.trajectoryStations[0]?.md.measuredDepth.uom ||
    trajectory.defaultMdDatum?.md?.uom ||
    '—'
  );
}

function resolveIntensityUom(trajectory: ReadonlyDeep<TTrajectory>): string {
  return trajectory.trajectoryStations[0]?.dls?.uom || resolveDepthUom(trajectory);
}

function resolveCoordinatesUom(trajectory: ReadonlyDeep<TTrajectory>): string {
  const uom = trajectory.aziVertSect?.uom || trajectory.magDeclUsed?.uom || trajectory.gridConUsed?.uom;

  if (!uom) {
    return '°';
  }

  if (uom === 'dega' || uom === 'deg') {
    return '°';
  }

  return uom;
}

function resolveDatumLabel(trajectory: ReadonlyDeep<TTrajectory>, kind: 'md' | 'tvd'): string {
  const datum = kind === 'md' ? trajectory.defaultMdDatum : trajectory.defaultTvdDatum;

  if (!datum) {
    return '—';
  }

  return datum.citation?.title || formatTrajectoryText(datum.verticalCoordinate);
}

export function TrajectoryDetails({ trajectory, parentTitle }: Props) {
  const { t } = useTranslation();

  if (!trajectory) {
    return <div className={styles.detailsEmpty}>{t('ListSidebar.noSelection')}</div>;
  }

  const isActive = Boolean(trajectory.active ?? trajectory.definitive);
  const isFinal = Boolean(trajectory.finalTraj);
  const organization = trajectory.citation.originator;

  return (
    <div className={styles.details}>
      <section className={styles.section}>
        <DetailField label={t('ListSidebar.fields.name')} value={formatTrajectoryText(trajectory.citation.title)} />
        <DetailField
          label={t('ListSidebar.fields.description')}
          value={formatTrajectoryText(trajectory.citation.description)}
        />
        <DetailField label={t('ListSidebar.fields.organization')} value={formatTrajectoryText(organization)} />
        <div className={styles.checkboxes}>
          <Checkbox checked={isActive} disabled size="s">
            {t('ListSidebar.active')}
          </Checkbox>
          <Checkbox checked={isFinal} disabled size="s">
            {t('ListSidebar.final')}
          </Checkbox>
        </div>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.tieIn')}</h3>
        <DetailField label={t('ListSidebar.fields.parentTrajectory')} value={formatTrajectoryText(parentTitle)} />
        <DetailField
          label={t('ListSidebar.fields.mdTieOn')}
          value={formatTrajectoryMeasure(trajectory.mdTieOn?.measuredDepth)}
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.azimuth')}</h3>
        <DetailRow label={t('ListSidebar.fields.aziRef')} value={formatTrajectoryText(trajectory.aziRef)} />
        <DetailRow
          label={t('ListSidebar.fields.gridConUsed')}
          value={formatTrajectoryMeasure(trajectory.gridConUsed)}
        />
        <DetailRow
          label={t('ListSidebar.fields.magDeclUsed')}
          value={formatTrajectoryMeasure(trajectory.magDeclUsed)}
        />
        <DetailRow
          label={t('ListSidebar.fields.aziVertSect')}
          value={formatTrajectoryMeasure(trajectory.aziVertSect)}
        />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.referencePoint')}</h3>
        <DetailRow label={t('ListSidebar.fields.mdDatum')} value={resolveDatumLabel(trajectory, 'md')} />
        <DetailRow label={t('ListSidebar.fields.tvdDatum')} value={resolveDatumLabel(trajectory, 'tvd')} />
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('ListSidebar.sections.units')}</h3>
        <DetailRow label={t('ListSidebar.fields.depthUnit')} value={resolveDepthUom(trajectory)} />
        <DetailRow label={t('ListSidebar.fields.intensityUnit')} value={resolveIntensityUom(trajectory)} />
        <DetailRow label={t('ListSidebar.fields.coordinatesUnit')} value={resolveCoordinatesUom(trajectory)} />
      </section>
    </div>
  );
}
